import { useState, useEffect } from "react";
import {
  X,
  Building2,
  Mail,
  MapPin,
  ImagePlus,
  Phone,
  Home,
  User,
} from "lucide-react";
import { createCompany, updateCompany } from "../../../services/companyService";
import { toast } from "react-hot-toast";
import { ChevronDown, Ship, Anchor } from "lucide-react";
import {
  getAllMenus,
  assignCompanyMenus,
  getCompanyMenuAssignments,
} from "../../../services/menuService";
import React from "react";

const AddEditCompanyModal = ({ open, onClose, onSubmit, company }) => {
  const [form, setForm] = useState({
    name: "",
    logo: "",
    location: "",
    address: "",
    phone: "",
    email: "",
    primaryContactName: "",
    primaryContactPhone: "",
    primaryContactEmail: "",
    secondaryContactName: "",
    secondaryContactPhone: "",
    secondaryContactEmail: "",
    menuPermissions: {},
    emissionTarget: {
      co2: "",
      ch4: "",
      n20: "",
      c02equivalent: "",
      sox: "",
      nox: "",
      pm: "",
    },
    kpiTarget: {
      kpiDistance: "",
      kpiSpeed: "",
      kpiUtilizationRate: ""
    },
    co2CostPerTon: "",
    plannedFuelConsumption: "",
    plannedVoyageSpeed: "",
    kpiWeights: {
      ciiWeight: "",
      fuelEUWeight: "",
      etsWeight: "",
    },
    scoreWeights: {
      emissionWeight: "",
      operationalWeight: "",
    },
    // chartedTarget: ""
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [menus, setMenus] = useState([]);
  const [loadingMenus, setLoadingMenus] = useState(false);

  useEffect(() => {
    const fetchMenus = async () => {
      setLoadingMenus(true);
      try {
        const menusResponse = await getAllMenus();

        if (!menusResponse.data) {
          throw new Error("No menu data received");
        }

        const initialPermissions = {};
        const initializeMenuPermissions = (menu) => {
          initialPermissions[menu._id] = {
            admin: false,
            user: false,
            readOnly: false,
          };
          if (menu.children && menu.children.length > 0) {
            menu.children.forEach((child) => {
              initialPermissions[child._id] = {
                admin: false,
                user: false,
                readOnly: false,
              };
            });
          }
        };
        menusResponse.data.forEach(initializeMenuPermissions);

        if (company) {
          try {
            const assignmentsResponse = await getCompanyMenuAssignments(
              company._id
            );
            // if (assignmentsResponse) {
            assignmentsResponse.flatMenus.forEach((menu) => {
              if (initialPermissions[menu._id]) {
                initialPermissions[menu._id] = {
                  admin: menu.admin ?? false,
                  user: menu.user ?? false,
                  readOnly: menu.readOnly ?? false,
                };
              } else {
                console.warn("Menu not found in initialPermissions:", menu._id);
              }
            });
            // }
          } catch (error) {
            console.warn(
              "Failed to fetch menu assignments, using defaults:",
              error
            );
          }
        }

        setMenus(menusResponse.data);
        setForm({
          name: company?.name || "",
          logo: company?.logo
            ? `${process.env.REACT_APP_API_BASE_URL}/${company.logo}`
            : "",
          location: company?.location || "",
          address: company?.address || "",
          phone: company?.phone || "",
          email: company?.email || "",
          primaryContactName: company?.primaryContactName || "",
          primaryContactPhone: company?.primaryContactPhone || "",
          primaryContactEmail: company?.primaryContactEmail || "",
          secondaryContactName: company?.secondaryContactName || "",
          secondaryContactPhone: company?.secondaryContactPhone || "",
          secondaryContactEmail: company?.secondaryContactEmail || "",
          logoFile: null,
          emissionTarget: company?.emissionTarget || {
            co2: "",
            ch4: "",
            n20: "",
            c02equivalent: "",
            sox: "",
            nox: "",
            pm: "",
          },
          kpiTarget: company?.kpiTarget || {
            kpiDistance: "",
            kpiSpeed: "",
            kpiUtilizationRate: "",
          },
          co2CostPerTon: company?.co2CostPerTon ?? "",
          plannedFuelConsumption: company?.plannedFuelConsumption ?? "",
          plannedVoyageSpeed: company?.plannedVoyageSpeed ?? "",
          kpiWeights: {
            ciiWeight: company?.kpiWeights?.ciiWeight ?? "",
            fuelEUWeight: company?.kpiWeights?.fuelEUWeight ?? "",
            etsWeight: company?.kpiWeights?.etsWeight ?? "",
          },
          scoreWeights: {
            emissionWeight: company?.scoreWeights?.emissionWeight ?? "",
            operationalWeight: company?.scoreWeights?.operationalWeight ?? "",
          },
          // chartedTarget: company?.chartedTarget || "",
          menuPermissions: initialPermissions,
        });
      } catch (error) {
        toast.error("Failed to load menus");
      } finally {
        setLoadingMenus(false);
      }
    };

    if (open) {
      fetchMenus();
      setErrors({});
      setShowAdvanced(false);
    }
  }, [open, company]);

  const handleMenuPermissionChange = (menuId, role, checked) => {
    setForm((prev) => {
      const updatedPermissions = { ...prev.menuPermissions };

      const menu = menus.find((m) => m._id === menuId);

      updatedPermissions[menuId] = {
        ...updatedPermissions[menuId],
        [role]: checked,
      };

      if (menu && menu.children && menu.children.length > 0) {
        menu.children.forEach((child) => {
          updatedPermissions[child._id] = {
            ...updatedPermissions[child._id],
            [role]: checked,
          };
        });
      }

      return {
        ...prev,
        menuPermissions: updatedPermissions,
      };
    });
  };

  const submitMenuPermissions = async (companyId) => {
    try {
      const permissionsData = Object.entries(form.menuPermissions)
        .filter(([menuId]) => {
          const valid = isValidObjectId(menuId);
          return valid;
        })
        .map(([menuId, permissions]) => {
          const roles = [
            ...(permissions.admin ? ["admin"] : []),
            ...(permissions.user ? ["user"] : []),
            ...(permissions.readOnly ? ["readonly"] : []),
          ];

          const isActive =
            permissions.admin || permissions.user || permissions.readOnly;

          return {
            menuId,
            roles,
            isActive,
          };
        });

      const response = await assignCompanyMenus(companyId, {
        permissions: permissionsData,
      });

      if (response.status === "success") {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Error in submitMenuPermissions:", error);
      return false;
    }
  };

  function isValidObjectId(id) {
    return /^[0-9a-fA-F]{24}$/.test(id);
  }

  const getApiErrorMessage = (errorValue) => {
    if (!errorValue) return "";
    if (typeof errorValue === "string") return errorValue;
    if (Array.isArray(errorValue)) {
      return errorValue.map(getApiErrorMessage).filter(Boolean).join(", ");
    }
    return errorValue.message || errorValue.msg || "";
  };

  const getApiErrorField = (errorKey, errorValue) => {
    if (typeof errorValue === "object" && errorValue !== null) {
      return (
        errorValue.field ||
        errorValue.path ||
        errorValue.param ||
        errorValue.name ||
        errorKey
      );
    }

    return errorKey;
  };

  const normalizeApiFieldPath = (fieldPath = "") =>
    fieldPath
      .replace(/\[(\w+)\]/g, ".$1")
      .replace(/^body\./, "")
      .replace(/^data\./, "");

  const fieldErrorTargets = {
    "kpiWeights.ciiWeight": ["kpiWeights", "ciiWeight"],
    ciiWeight: ["kpiWeights", "ciiWeight"],
    "kpiWeights.fuelEUWeight": ["kpiWeights", "fuelEUWeight"],
    fuelEUWeight: ["kpiWeights", "fuelEUWeight"],
    "kpiWeights.etsWeight": ["kpiWeights", "etsWeight"],
    etsWeight: ["kpiWeights", "etsWeight"],
    "kpiWeights.total": ["kpiWeights", "total"],
    "scoreWeights.emissionWeight": ["scoreWeights", "emissionWeight"],
    emissionWeight: ["scoreWeights", "emissionWeight"],
    "scoreWeights.operationalWeight": ["scoreWeights", "operationalWeight"],
    operationalWeight: ["scoreWeights", "operationalWeight"],
    "scoreWeights.total": ["scoreWeights", "total"],
  };

  const inferWeightErrorTarget = (message = "") => {
    const normalizedMessage = message.toLowerCase();

    if (normalizedMessage.includes("cii")) return ["kpiWeights", "ciiWeight"];
    if (normalizedMessage.includes("fueleu")) {
      return ["kpiWeights", "fuelEUWeight"];
    }
    if (normalizedMessage.includes("ets")) return ["kpiWeights", "etsWeight"];
    if (normalizedMessage.includes("emission weight")) {
      return ["scoreWeights", "emissionWeight"];
    }
    if (normalizedMessage.includes("operational weight")) {
      return ["scoreWeights", "operationalWeight"];
    }
    if (normalizedMessage.includes("kpi weight")) {
      return ["kpiWeights", "total"];
    }
    if (normalizedMessage.includes("score weight")) {
      return ["scoreWeights", "total"];
    }

    return null;
  };

  const applyApiFieldErrors = (apiResponse) => {
    const apiErrors =
      apiResponse?.errors ||
      apiResponse?.error ||
      apiResponse?.data?.errors ||
      apiResponse?.data?.error;
    const nextErrors = {};
    const addFieldError = (target, message) => {
      if (!target || !message) return;
      const [group, field] = target;

      nextErrors[group] = {
        ...nextErrors[group],
        [field]: message,
      };
    };

    if (Array.isArray(apiErrors)) {
      apiErrors.forEach((errorItem) => {
        const message = getApiErrorMessage(errorItem);
        const field = normalizeApiFieldPath(getApiErrorField("", errorItem));
        const target = fieldErrorTargets[field] || inferWeightErrorTarget(message);
        addFieldError(target, message);
      });
    } else if (apiErrors && typeof apiErrors === "object") {
      Object.entries(apiErrors).forEach(([errorKey, errorValue]) => {
        const message = getApiErrorMessage(errorValue);
        const field = normalizeApiFieldPath(
          getApiErrorField(errorKey, errorValue)
        );
        const target = fieldErrorTargets[field] || inferWeightErrorTarget(message);
        addFieldError(target, message);
      });
    }

    const responseMessage = getApiErrorMessage(apiResponse?.message);
    addFieldError(inferWeightErrorTarget(responseMessage), responseMessage);

    if (Object.keys(nextErrors).length === 0) {
      return false;
    }

    setErrors((prev) => ({
      ...prev,
      ...nextErrors,
    }));
    return true;
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (name === "logo") {
      if (files && files[0]) {
        if (form.logo && form.logo.startsWith("blob:")) {
          URL.revokeObjectURL(form.logo);
        }
        const previewUrl = URL.createObjectURL(files[0]);
        setForm((prev) => ({
          ...prev,
          logo: previewUrl,
          logoFile: files[0],
        }));
      }
    }else {
      setForm((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }

    // if (name === "chartedTarget") {
    //   if (/^[0-9]*\.?[0-9]*$/.test(value)) {
    //     setForm((prev) => ({
    //       ...prev,
    //       [name]: value,
    //     }));
    //   }
    // } 
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleEmissionChange = (field, value) => {
    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setForm((prev) => ({
        ...prev,
        emissionTarget: {
          ...prev.emissionTarget,
          [field]: value,
        },
      }));

      if (errors.emissionTarget?.[field]) {
        setErrors((prev) => ({
          ...prev,
          emissionTarget: {
            ...prev.emissionTarget,
            [field]: "",
          },
        }));
      }
    }
  };

  const handleKpiChange = (field, value) => {
    // console.log("value", value);
    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setForm((prev) => ({
        ...prev,
        kpiTarget: {
          ...prev.kpiTarget,
          [field]: value,
        },
      }));

      if (errors.kpiTarget?.[field]) {
        setErrors((prev) => ({
          ...prev,
          kpiTarget: {
            ...prev.kpiTarget,
            [field]: "",
          },
        }));
      }
    }
  };

  const handleNumericFieldChange = (field, value) => {
    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setForm((prev) => ({
        ...prev,
        [field]: value,
      }));

      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    }
  };

  const handleKpiWeightChange = (field, value) => {
    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setForm((prev) => ({
        ...prev,
        kpiWeights: {
          ...prev.kpiWeights,
          [field]: value,
        },
      }));

      if (errors.kpiWeights?.[field]) {
        setErrors((prev) => ({
          ...prev,
          kpiWeights: {
            ...prev.kpiWeights,
            [field]: "",
          },
        }));
      }
    }
  };

  const handleScoreWeightChange = (field, value) => {
    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setForm((prev) => ({
        ...prev,
        scoreWeights: {
          ...prev.scoreWeights,
          [field]: value,
        },
      }));

      if (errors.scoreWeights?.[field]) {
        setErrors((prev) => ({
          ...prev,
          scoreWeights: {
            ...prev.scoreWeights,
            [field]: "",
          },
        }));
      }
    }
  }

  const validateForm = () => {
    const newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = "Company name is required";
    }

    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!form.location.trim()) {
      newErrors.location = "Location is required";
    }

    if (form.phone && !/^[\d\s\+\-\(\)]{6,20}$/.test(form.phone)) {
      newErrors.phone = "Invalid phone number format";
    }

    if (
      form.primaryContactPhone &&
      !/^[\d\s\+\-\(\)]{6,20}$/.test(form.primaryContactPhone)
    ) {
      newErrors.primaryContactPhone = "Invalid phone number format";
    }

    if (
      form.secondaryContactPhone &&
      !/^[\d\s\+\-\(\)]{6,20}$/.test(form.secondaryContactPhone)
    ) {
      newErrors.secondaryContactPhone = "Invalid phone number format";
    }

    if (
      form.primaryContactEmail &&
      !/\S+@\S+\.\S+/.test(form.primaryContactEmail)
    ) {
      newErrors.primaryContactEmail = "Invalid email format";
    }

    if (
      form.secondaryContactEmail &&
      !/\S+@\S+\.\S+/.test(form.secondaryContactEmail)
    ) {
      newErrors.secondaryContactEmail = "Invalid email format";
    }

    if (form.address && form.address.length > 500) {
      newErrors.address = "Address cannot exceed 500 characters";
    }

    if (form.logoFile) {
      const validImageTypes = ["image/jpeg", "image/png"];
      if (!validImageTypes.includes(form.logoFile.type)) {
        newErrors.logo = "Only JPG and PNG images are allowed";
      } else if (form.logoFile.size > 2 * 1024 * 1024) {
        newErrors.logo = "Image size must be less than 2MB";
      }
    }

    // if (form.chartedTarget === ""){
    //   newErrors.chartedTarget = "Charted Target Is Required";
    // }

    // ---- Emission validation ----
    const validateEmissionValue = (value, fieldName) => {
      if (value === "" || value === null || value === undefined) {
        return `${fieldName} is required`;
      }
      if (isNaN(value) || parseFloat(value) < 0) {
        return `Enter a valid positive number for ${fieldName}`;
      }
      return "";
    };

    const validatePositiveNumber = (value, fieldName) => {
      if (value === "" || value === null || value === undefined) {
        return `${fieldName} is required`;
      }
      if (isNaN(value) || parseFloat(value) < 0) {
        return `Enter a valid positive number for ${fieldName}`;
      }
      return "";
    };

    newErrors.kpiTarget = {
      kpiDistance: validateEmissionValue(form.emissionTarget.co2, "Distance"),
      kpiSpeed: validateEmissionValue(form.emissionTarget.ch4, "Speed"),
      kpiUtilizationRate: validateEmissionValue(form.emissionTarget.n20, "Utilization Rate"),
    }

    newErrors.emissionTarget = {
      co2: validateEmissionValue(form.emissionTarget.co2, "CO₂"),
      ch4: validateEmissionValue(form.emissionTarget.ch4, "CH₄"),
      n20: validateEmissionValue(form.emissionTarget.n20, "N₂O"),
      c02equivalent: validateEmissionValue(
        form.emissionTarget.c02equivalent,
        "CO₂ Equivalent"
      ),
      sox: validateEmissionValue(form.emissionTarget.sox, "SOx"),
      nox: validateEmissionValue(form.emissionTarget.nox, "NOx"),
      pm: validateEmissionValue(form.emissionTarget.pm, "PM"),
    };

    if (
      !newErrors.emissionTarget.co2 &&
      !newErrors.emissionTarget.ch4 &&
      !newErrors.emissionTarget.n20 &&
      !newErrors.emissionTarget.c02equivalent &&
      !newErrors.emissionTarget.sox &&
      !newErrors.emissionTarget.nox &&
      !newErrors.emissionTarget.pm
    ) {
      delete newErrors.emissionTarget;
    }

    if (
      !newErrors.kpiTarget.kpiDistance &&
      !newErrors.kpiTarget.kpiSpeed &&
      !newErrors.kpiTarget.kpiUtilizationRate
    ) {
      delete newErrors.kpiTarget;
    }

    
    const co2CostPerTonError = validatePositiveNumber(
      form.co2CostPerTon,
      "CO2 Cost Per Ton"
    );
    if (co2CostPerTonError) {
      newErrors.co2CostPerTon = co2CostPerTonError;
    }

    const plannedFuelConsumptionError = validatePositiveNumber(
      form.plannedFuelConsumption,
      "Planned Fuel Consumption"
    );
    if (plannedFuelConsumptionError) {
      newErrors.plannedFuelConsumption = plannedFuelConsumptionError;
    }

    const plannedVoyageSpeedError = validatePositiveNumber(
      form.plannedVoyageSpeed,
      "Planned Voyage Speed"
    );
    if (plannedVoyageSpeedError) {
      newErrors.plannedVoyageSpeed = plannedVoyageSpeedError;
    }


    newErrors.kpiWeights = {};
    newErrors.kpiWeights.ciiWeight = validatePositiveNumber(
      form.kpiWeights.ciiWeight,
      "CII Weight"
    );

    newErrors.kpiWeights.fuelEUWeight = validatePositiveNumber(
      form.kpiWeights.fuelEUWeight,
      "FuelEU Weight"
    );

    newErrors.kpiWeights.etsWeight = validatePositiveNumber(
      form.kpiWeights.etsWeight,
      "ETS Weight"
    );

    if (
      !newErrors.kpiWeights.ciiWeight &&
      !newErrors.kpiWeights.fuelEUWeight &&
      !newErrors.kpiWeights.etsWeight
    ) {
      delete newErrors.kpiWeights;
    }

   
    newErrors.scoreWeights = {};
    newErrors.scoreWeights.emissionWeight = validatePositiveNumber(
      form.scoreWeights.emissionWeight,
      "Emission Weight"
    );

    newErrors.scoreWeights.operationalWeight = validatePositiveNumber(
      form.scoreWeights.operationalWeight,
      "Operational Weight"
    );

    if (
      !newErrors.scoreWeights.emissionWeight &&
      !newErrors.scoreWeights.operationalWeight
    ) {
      delete newErrors.scoreWeights;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();

      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("location", form.location);
      formData.append("phone", form.phone);
      formData.append("address", form.address);
      formData.append("primaryContactName", form.primaryContactName);
      formData.append("primaryContactPhone", form.primaryContactPhone);
      formData.append("primaryContactEmail", form.primaryContactEmail);
      formData.append("secondaryContactName", form.secondaryContactName);
      formData.append("secondaryContactPhone", form.secondaryContactPhone);
      formData.append("secondaryContactEmail", form.secondaryContactEmail);
      formData.append(
        "emissionTarget[co2]",
        parseFloat(form.emissionTarget.co2) || 0
      );
      formData.append(
        "emissionTarget[ch4]",
        parseFloat(form.emissionTarget.ch4) || 0
      );
      formData.append(
        "emissionTarget[n20]",
        parseFloat(form.emissionTarget.n20) || 0
      );
      formData.append(
        "emissionTarget[c02equivalent]",
        parseFloat(form.emissionTarget.c02equivalent) || 0
      );
      formData.append(
        "emissionTarget[sox]",
        parseFloat(form.emissionTarget.sox) || 0
      );
      formData.append(
        "emissionTarget[nox]",
        parseFloat(form.emissionTarget.nox) || 0
      );
      formData.append(
        "emissionTarget[pm]",
        parseFloat(form.emissionTarget.pm) || 0
      );

      formData.append(
        "kpiTarget[kpiDistance]",
        parseFloat(form.kpiTarget.kpiDistance) || 0
      );
      formData.append(
        "kpiTarget[kpiSpeed]",
        parseFloat(form.kpiTarget.kpiSpeed) || 0
      );
      formData.append(
        "kpiTarget[kpiUtilizationRate]",
        parseFloat(form.kpiTarget.kpiUtilizationRate) || 0
      );

      
      formData.append("co2CostPerTon", parseFloat(form.co2CostPerTon) || 0);
      formData.append("plannedFuelConsumption", parseFloat(form.plannedFuelConsumption) || 0);
      formData.append("plannedVoyageSpeed", parseFloat(form.plannedVoyageSpeed) || 0);

     
      formData.append("kpiWeights[ciiWeight]", parseFloat(form.kpiWeights.ciiWeight) || 0);
      formData.append("kpiWeights[fuelEUWeight]", parseFloat(form.kpiWeights.fuelEUWeight) || 0);
      formData.append("kpiWeights[etsWeight]", parseFloat(form.kpiWeights.etsWeight) || 0);

      
      formData.append("scoreWeights[emissionWeight]", parseFloat(form.scoreWeights.emissionWeight) || 0);
      formData.append("scoreWeights[operationalWeight]", parseFloat(form.scoreWeights.operationalWeight) || 0);

      // Use logoFile if it exists
      if (form.logoFile) {
        formData.append("logo", form.logoFile);
      }

      let response;
      let companyId;
      if (company && company._id) {
        response = await updateCompany(company?._id, formData);
        companyId = company._id;
      } else {
        response = await createCompany(formData);
        companyId = response._id || response.data?._id;
      }

      if (response.status === "success") {
        if (companyId) {
          await submitMenuPermissions(companyId);
        }
       
        toast.success(
          company
            ? "Company Updated Successfully"
            : "Company Created Successfully"
        );
        onSubmit(response.data || response);
        onClose();
      } else {
        const hasFieldErrors = applyApiFieldErrors(response);
        if (!hasFieldErrors) {
          toast.error(response.message || "Something went wrong");
        }
      }
    } catch (error) {
      const apiResponse = error.response?.data;
      const hasFieldErrors = applyApiFieldErrors(apiResponse);
      if (!hasFieldErrors) {
        toast.error(apiResponse?.message || error.message || "Error submitting company");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh] overflow-hidden">
        <div className="px-6 py-5 border-t border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800">Menu Permissions</h3>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              {showAdvanced ? "Hide Advanced" : "Show Advanced"}
              <ChevronDown
                className={`w-4 h-4 transition-transform ${showAdvanced ? "rotate-180" : ""
                  }`}
              />
            </button>
          </div>

          {showAdvanced && (
            <div className="space-y-4">
              {loadingMenus ? (
                <div className="flex justify-center py-4">
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="overflow-auto max-h-96">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Menu
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Admin
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          User
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Read Only
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {menus.map((menu) => (
                        <React.Fragment key={menu._id}>
                          {/* Parent Menu */}
                          <tr className="bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center gap-2">
                              <span className="w-5 h-5 flex items-center justify-center">
                                {menu.icon === "fleet" && (
                                  <Ship className="w-4 h-4" />
                                )}
                                {menu.icon === "vessel" && (
                                  <Anchor className="w-4 h-4" />
                                )}
                                {/* Need to add compliance and admin icon */}
                              </span>
                              {menu.title}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <input
                                type="checkbox"
                                checked={
                                  form.menuPermissions[menu._id]?.admin || false
                                }
                                onChange={(e) =>
                                  handleMenuPermissionChange(
                                    menu._id,
                                    "admin",
                                    e.target.checked
                                  )
                                }
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <input
                                type="checkbox"
                                checked={
                                  form.menuPermissions[menu._id]?.user || false
                                }
                                onChange={(e) =>
                                  handleMenuPermissionChange(
                                    menu._id,
                                    "user",
                                    e.target.checked
                                  )
                                }
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <input
                                type="checkbox"
                                checked={
                                  form.menuPermissions[menu._id]?.readOnly ||
                                  false
                                }
                                onChange={(e) =>
                                  handleMenuPermissionChange(
                                    menu._id,
                                    "readOnly",
                                    e.target.checked
                                  )
                                }
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                            </td>
                          </tr>

                          {/* Child Menus */}
                          {menu.children?.map((child) => (
                            <tr
                              key={child._id}
                              className="border-t border-gray-100"
                            >
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 pl-10">
                                <div className="flex items-center gap-2">
                                  <span className="w-5 h-5 flex items-center justify-center opacity-0">
                                    <Anchor className="w-4 h-4" />
                                  </span>
                                  {child.title}
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <input
                                  type="checkbox"
                                  checked={
                                    form.menuPermissions[child._id]?.admin ||
                                    false
                                  }
                                  onChange={(e) =>
                                    handleMenuPermissionChange(
                                      child._id,
                                      "admin",
                                      e.target.checked
                                    )
                                  }
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <input
                                  type="checkbox"
                                  checked={
                                    form.menuPermissions[child._id]?.user ||
                                    false
                                  }
                                  onChange={(e) =>
                                    handleMenuPermissionChange(
                                      child._id,
                                      "user",
                                      e.target.checked
                                    )
                                  }
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <input
                                  type="checkbox"
                                  checked={
                                    form.menuPermissions[child._id]?.readOnly ||
                                    false
                                  }
                                  onChange={(e) =>
                                    handleMenuPermissionChange(
                                      child._id,
                                      "readOnly",
                                      e.target.checked
                                    )
                                  }
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
        {/* FORM HEADER */}
        <div className="relative bg-white rounded-t-3xl border-b border-gray-200 px-6 pt-5 pb-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
              <Building2 className="w-5 h-5 text-gray-600" />
            </div>
            <div className="flex flex-col">
              <h2 className="text-lg font-semibold text-gray-800 tracking-tight">
                {company ? "Edit Company" : "Add New Company"}
              </h2>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-100 transition"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* BODY */}
        <div className="flex-1 px-6 py-5 overflow-y-auto space-y-6 text-sm text-gray-700 bg-white">
          {/* Company Name */}
          <div>
            <label className="mb-1 block font-medium">Company Name</label>
            <div className="relative">
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Oceanic Corp"
                className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
                  ${errors.name
                    ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                    : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                  }`}
              />
              <Building2 className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
            </div>
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="mb-1 block font-medium">Email</label>
            <div className="relative">
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="contact@oceanic.com"
                className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
                  ${errors.email
                    ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                    : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                  }`}
              />
              <Mail className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
            </div>
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          {/* Location */}
          <div>
            <label className="mb-1 block font-medium">Location</label>
            <div className="relative">
              <input
                type="text"
                name="location"
                value={form.location}
                onChange={handleChange}
                placeholder="Singapore"
                className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
                  ${errors.location
                    ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                    : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                  }`}
              />
              <MapPin className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
            </div>
            {errors.location && (
              <p className="text-red-500 text-xs mt-1">{errors.location}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="mb-1 block font-medium">Phone</label>
            <div className="relative">
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="+65 1234 5678"
                className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
                  ${errors.phone
                    ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                    : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                  }`}
              />
              <Phone className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
            </div>
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            )}
          </div>

          {/* Primary Contact */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Primary Contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="mb-1 block text-sm">Name</label>
                <div className="relative">
                  <input
                    type="text"
                    name="primaryContactName"
                    value={form.primaryContactName}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
                      ${errors.primaryContactName
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  <User className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm">Phone</label>
                <div className="relative">
                  <input
                    type="tel"
                    name="primaryContactPhone"
                    value={form.primaryContactPhone}
                    onChange={handleChange}
                    placeholder="+65 1234 5678"
                    className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
                      ${errors.primaryContactPhone
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  <Phone className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                </div>
                {errors.primaryContactPhone && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.primaryContactPhone}
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm">Email</label>
                <div className="relative">
                  <input
                    type="email"
                    name="primaryContactEmail"
                    value={form.primaryContactEmail}
                    onChange={handleChange}
                    placeholder="john@oceanic.com"
                    className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
                      ${errors.primaryContactEmail
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  <Mail className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                </div>
                {errors.primaryContactEmail && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.primaryContactEmail}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Secondary Contact */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Secondary Contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="mb-1 block text-sm">Name</label>
                <div className="relative">
                  <input
                    type="text"
                    name="secondaryContactName"
                    value={form.secondaryContactName}
                    onChange={handleChange}
                    placeholder="Jane Smith"
                    className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
                      ${errors.secondaryContactName
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  <User className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm">Phone</label>
                <div className="relative">
                  <input
                    type="tel"
                    name="secondaryContactPhone"
                    value={form.secondaryContactPhone}
                    onChange={handleChange}
                    placeholder="+65 9876 5432"
                    className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
                      ${errors.secondaryContactPhone
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  <Phone className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                </div>
                {errors.secondaryContactPhone && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.secondaryContactPhone}
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm">Email</label>
                <div className="relative">
                  <input
                    type="email"
                    name="secondaryContactEmail"
                    value={form.secondaryContactEmail}
                    onChange={handleChange}
                    placeholder="jane@oceanic.com"
                    className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
                      ${errors.secondaryContactEmail
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  <Mail className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                </div>
                {errors.secondaryContactEmail && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.secondaryContactEmail}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Logo Upload */}
          <div>
            <label className="mb-1 block font-medium">Company Logo</label>
            <div className="relative">
              <input
                type="file"
                name="logo"
                id="company-logo-upload"
                accept="image/*"
                onChange={handleChange}
                className="hidden"
              />

              <label
                htmlFor="company-logo-upload"
                className={`flex items-center justify-between w-full rounded-lg border px-4 py-3 bg-white cursor-pointer transition-all
                  ${errors.logo
                    ? "border-red-300"
                    : "border-gray-300 hover:border-[#27ade3]"
                  }
                  ${form.logo ? "border-green-200 bg-green-50" : ""}`}
              >
                <div className="flex items-center gap-3">
                  {form.logo ? (
                    <>
                      <div className="w-10 h-10 rounded-md overflow-hidden border border-gray-200">
                        <img
                          src={form.logo}
                          alt="Company logo"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800 truncate max-w-[180px]">
                          {form.logoFile ? form.logoFile.name : "Current logo"}
                        </p>
                        {form.logoFile && (
                          <p className="text-xs text-gray-500">
                            {(form.logoFile.size / 1024).toFixed(1)} KB
                          </p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-md border-2 border-dashed border-gray-300 flex items-center justify-center">
                        <ImagePlus className="w-5 h-5 text-gray-400" />
                      </div>
                      <span className="text-sm text-gray-600">
                        Choose an image...
                      </span>
                    </div>
                  )}
                </div>
              </label>

              {form.logo && (
                <button
                  type="button"
                  onClick={() =>
                    setForm((prev) => ({ ...prev, logo: "", logoFile: null }))
                  }
                  className="absolute right-10 top-5 p-1 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}

              {errors.logo && (
                <p className="text-red-500 text-xs mt-1">{errors.logo}</p>
              )}
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="mb-1 block font-medium">Address</label>
            <div className="relative">
              <textarea
                name="address"
                value={form.address}
                onChange={handleChange}
                placeholder="123 Marina Bay, Singapore 018956"
                rows={3}
                className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
                  ${errors.address
                    ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                    : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                  }`}
              />
              <Home className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
            </div>
            {errors.address && (
              <p className="text-red-500 text-xs mt-1">{errors.address}</p>
            )}
          </div>

          {/* Emission Targets */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">
              Emission Targets (g/kWh)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm">CO₂</label>
                <div className="relative">
                  <input
                    type="text"
                    name="emissionTarget.co2"
                    value={form.emissionTarget.co2}
                    onChange={(e) =>
                      handleEmissionChange("co2", e.target.value)
                    }
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.emissionTarget?.co2
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  <span className="absolute right-3 top-3 h-4 w-4 text-gray-400 text-sm">
                    g
                  </span>
                  {errors.emissionTarget?.co2 && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.emissionTarget.co2}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm">CH₄</label>
                <div className="relative">
                  <input
                    type="text"
                    name="emissionTarget.ch4"
                    value={form.emissionTarget.ch4}
                    onChange={(e) =>
                      handleEmissionChange("ch4", e.target.value)
                    }
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.emissionTarget?.ch4
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  <span className="absolute right-3 top-3 h-4 w-4 text-gray-400 text-sm">
                    g
                  </span>
                  {errors.emissionTarget?.ch4 && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.emissionTarget.ch4}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm">N₂O</label>
                <div className="relative">
                  <input
                    type="text"
                    name="emissionTarget.n20"
                    value={form.emissionTarget.n20}
                    onChange={(e) =>
                      handleEmissionChange("n20", e.target.value)
                    }
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.emissionTarget?.n20
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  <span className="absolute right-3 top-3 h-4 w-4 text-gray-400 text-sm">
                    g
                  </span>
                  {errors.emissionTarget?.n20 && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.emissionTarget.n20}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm">CO₂ Equivalent</label>
                <div className="relative">
                  <input
                    type="text"
                    name="emissionTarget.c02equivalent"
                    value={form.emissionTarget.c02equivalent}
                    onChange={(e) =>
                      handleEmissionChange("c02equivalent", e.target.value)
                    }
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.emissionTarget?.c02equivalent
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  <span className="absolute right-3 top-3 h-4 w-4 text-gray-400 text-sm">
                    g
                  </span>
                  {errors.emissionTarget?.c02equivalent && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.emissionTarget.c02equivalent}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm">SOx</label>
                <div className="relative">
                  <input
                    type="text"
                    name="emissionTarget.sox"
                    value={form.emissionTarget.sox}
                    onChange={(e) =>
                      handleEmissionChange("sox", e.target.value)
                    }
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.emissionTarget?.sox
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  <span className="absolute right-3 top-3 h-4 w-4 text-gray-400 text-sm">
                    g
                  </span>
                  {errors.emissionTarget?.sox && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.emissionTarget.sox}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm">NOx</label>
                <div className="relative">
                  <input
                    type="text"
                    name="emissionTarget.nox"
                    value={form.emissionTarget.nox}
                    onChange={(e) =>
                      handleEmissionChange("nox", e.target.value)
                    }
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.emissionTarget?.nox
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  <span className="absolute right-3 top-3 h-4 w-4 text-gray-400 text-sm">
                    g
                  </span>
                  {errors.emissionTarget?.nox && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.emissionTarget.nox}
                    </p>
                  )}
                </div>
              </div>

              {/* PM */}
              <div>
                <label className="mb-1 block text-sm">PM</label>
                <div className="relative">
                  <input
                    type="text"
                    name="emissionTarget.pm"
                    value={form.emissionTarget.pm}
                    onChange={(e) => handleEmissionChange("pm", e.target.value)}
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.emissionTarget?.pm
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  <span className="absolute right-3 top-3 h-4 w-4 text-gray-400 text-sm">
                    g
                  </span>
                  {errors.emissionTarget?.pm && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.emissionTarget.pm}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* KPI trends target values */}
          {/* <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">
              KPI Trends Target Values
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm">Distance (NM)</label>
                <div className="relative">
                  <input
                    type="text"
                    name="kpiTarget.kpiDistance"
                    value={form.kpiTarget.kpiDistance}
                    onChange={(e) =>
                      handleKpiChange("kpiDistance", e.target.value)
                    }
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.kpiTarget?.kpiDistance
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  {errors.kpiTarget?.kpiDistance && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.kpiTarget.kpiDistance}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm">Speed (Knots)</label>
                <div className="relative">
                  <input
                    type="text"
                    name="kpiTarget.kpiSpeed"
                    value={form.kpiTarget.kpiSpeed}
                    onChange={(e) =>
                      handleKpiChange("kpiSpeed", e.target.value)
                    }
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.kpiTarget?.kpiSpeed
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  {errors.kpiTarget?.kpiSpeed && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.kpiTarget.kpiSpeed}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm">Utilization Rate (%)</label>
                <div className="relative">
                  <input
                    type="text"
                    name="kpiTarget.kpiUtilizationRate"
                    value={form.kpiTarget.kpiUtilizationRate}
                    onChange={(e) =>
                      handleKpiChange("kpiUtilizationRate", e.target.value)
                    }
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.kpiTarget?.kpiUtilizationRate
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  {errors.kpiTarget?.kpiUtilizationRate && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.kpiTarget.kpiUtilizationRate}
                    </p>
                  )}
                </div>
              </div>

            </div>
          </div> */}

          {/* Operational Parameters */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Operational Parameters</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="mb-1 block text-sm">CO₂ Cost Per Ton</label>
                <div className="relative">
                  <input
                    type="text"
                    value={form.co2CostPerTon}
                    onChange={(e) =>
                      handleNumericFieldChange("co2CostPerTon", e.target.value)
                    }
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.co2CostPerTon
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  <span className="absolute right-3 top-3 text-sm text-gray-400">t</span>
                </div>
                {errors.co2CostPerTon && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.co2CostPerTon}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm">Planned Fuel Consumption</label>
                <div className="relative">
                  <input
                    type="text"
                    value={form.plannedFuelConsumption}
                    onChange={(e) =>
                      handleNumericFieldChange("plannedFuelConsumption", e.target.value)
                    }
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.plannedFuelConsumption
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  <span className="absolute right-3 top-3 text-sm text-gray-400">t/day</span>
                </div>
                {errors.plannedFuelConsumption && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.plannedFuelConsumption}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm">Planned Voyage Speed</label>
                <div className="relative">
                  <input
                    type="text"
                    value={form.plannedVoyageSpeed}
                    onChange={(e) =>
                      handleNumericFieldChange("plannedVoyageSpeed", e.target.value)
                    }
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.plannedVoyageSpeed
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  <span className="absolute right-3 top-3 text-sm text-gray-400">knots</span>
                </div>
                {errors.plannedVoyageSpeed && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.plannedVoyageSpeed}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* KPI Weights and Score Weights Combined */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">
              KPI Weights
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm">CII Weight (%)</label>
                <div className="relative">
                  <input
                    type="text"
                    value={form.kpiWeights.ciiWeight}
                    onChange={(e) =>
                      handleKpiWeightChange("ciiWeight", e.target.value)
                    }
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.kpiWeights?.ciiWeight
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  {errors.kpiWeights?.ciiWeight && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.kpiWeights.ciiWeight}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm">FuelEU Weight (%)</label>
                <div className="relative">
                  <input
                    type="text"
                    value={form.kpiWeights.fuelEUWeight}
                    onChange={(e) =>
                      handleKpiWeightChange("fuelEUWeight", e.target.value)
                    }
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.kpiWeights?.fuelEUWeight
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  {errors.kpiWeights?.fuelEUWeight && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.kpiWeights.fuelEUWeight}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm">ETS Weight (%)</label>
                <div className="relative">
                  <input
                    type="text"
                    value={form.kpiWeights.etsWeight}
                    onChange={(e) =>
                      handleKpiWeightChange("etsWeight", e.target.value)
                    }
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.kpiWeights?.etsWeight
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  {errors.kpiWeights?.etsWeight && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.kpiWeights.etsWeight}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm">Emission Weight (%)</label>
                <div className="relative">
                  <input
                    type="text"
                    value={form.scoreWeights.emissionWeight}
                    onChange={(e) =>
                      handleScoreWeightChange("emissionWeight", e.target.value)
                    }
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.scoreWeights?.emissionWeight
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  {errors.scoreWeights?.emissionWeight && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.scoreWeights.emissionWeight}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm">Operational Weight (%)</label>
                <div className="relative">
                  <input
                    type="text"
                    value={form.scoreWeights.operationalWeight}
                    onChange={(e) =>
                      handleScoreWeightChange("operationalWeight", e.target.value)
                    }
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.scoreWeights?.operationalWeight
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  {errors.scoreWeights?.operationalWeight && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.scoreWeights.operationalWeight}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {errors.kpiWeights?.total && (
              <p className="text-red-500 text-xs mt-1">
                {errors.kpiWeights.total}
              </p>
            )}

            {errors.scoreWeights?.total && (
              <p className="text-red-500 text-xs mt-1">
                {errors.scoreWeights.total}
              </p>
            )}
          </div>

          {/* CHarted Targets values */}
          {/* <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">
              Charted Target Value
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div> */}
                {/* <label className="mb-1 block text-sm">Charted Target</label> */}
                {/* <div className="relative">
                  <input
                    type="text"
                    name="chartedTarget"
                    value={form.chartedTarget}
                    onChange={handleChange}
                    placeholder="0"
                    className={`w-full rounded-lg border px-4 py-2.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1
            ${errors.chartedTarget
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                      }`}
                  />
                  {errors.chartedTarget && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.chartedTarget}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div> */}

        </div>

        {/* FORM FOOTER */}
        <div className="bg-white rounded-b-3xl border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`px-5 py-2.5 text-sm rounded-lg text-white flex items-center gap-2 transition-colors font-medium 
              ${isSubmitting
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-[#1a325d] hover:bg-[#21426e]"
              }`}
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>{company ? "Updating..." : "Creating..."}</span>
              </>
            ) : (
              <>
                <span>{company ? "Update Company" : "Add Company"}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditCompanyModal;
